export interface CampaignCohortCounts {
  campaignId: number;
  campaignName: string;
  brandId: number;
  retailerName: string;
  status: string;
  cohorts: Record<string, number>;
  unplaced: number;
  suppressed: number;
  activations: number;
}

export interface PanelArmCounts {
  panelId: number;
  brandId: number;
  retailerName: string;
  version: number;
  drawnAt: string | null;
  arms: Record<string, Record<string, number>>;
}

export interface CohortFunnel {
  consented: number;
  placed: number;
  seeingOffer: number;
  activated: number;
  linkedCard: number;
}

export interface CohortTimelineDay {
  day: string;
  placed: number;
  moved: number;
  exited: number;
}

export interface CohortOverview {
  generatedAt: string;
  campaigns: CampaignCohortCounts[];
  panels: PanelArmCounts[];
  funnel: CohortFunnel;
  timeline: CohortTimelineDay[];
}

export type ChartMode = "light" | "dark";

export interface Series {
  key: string;
  colour: string;
}

export const ARMS = [
  { key: "TEST", label: "Test" },
  { key: "CONTROL", label: "Control" },
];
export const NO_COHORT = "No cohort";
export const OTHER_COHORTS = "Other cohorts";

const KNOWN_COHORTS = ["New", "Existing", "Lapsed"];
const MAX_COHORT_SERIES = 6;

// Validated categorical slots (fixed order) for the light and dark admin themes.
const COHORT_COLOURS: Record<ChartMode, string[]> = {
  light: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300"],
  dark: ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181", "#008300"],
};
const ARM_COLOURS: Record<ChartMode, Record<string, string>> = {
  light: { TEST: "#6250d6", CONTROL: "#eda100" },
  dark: { TEST: "#9085e9", CONTROL: "#c98500" },
};
const NEUTRAL: Record<ChartMode, string> = { light: "#a3a29c", dark: "#6f6e69" };
export const SINGLE_SERIES: Record<ChartMode, string> = { light: "#2a78d6", dark: "#3987e5" };

export const FUNNEL_STAGES: Array<{ key: keyof CohortFunnel; label: string }> = [
  { key: "consented", label: "Live open banking consent" },
  { key: "placed", label: "Placed in a cohort" },
  { key: "seeingOffer", label: "Seeing an offer" },
  { key: "activated", label: "Activated an offer" },
  { key: "linkedCard", label: "Live linked card" },
];

export const sum = (values: number[]): number => values.reduce((total, n) => total + n, 0);

export const share = (part: number, whole: number): string =>
  whole > 0 ? `${Math.round((part / whole) * 1000) / 10}%` : "0%";

const cohortRank = (name: string): number => {
  const index = KNOWN_COHORTS.indexOf(name);
  return index === -1 ? KNOWN_COHORTS.length : index;
};

/** Every cohort name on the page, New, Existing and Lapsed first, so a cohort keeps one colour in every chart. */
export const cohortNames = (overview: CohortOverview): string[] => {
  const groups = [
    ...overview.campaigns.map((campaign) => campaign.cohorts),
    ...overview.panels.flatMap((panel) => Object.values(panel.arms)),
  ];
  const names: Record<string, true> = {};
  for (const counts of groups) {
    for (const name of Object.keys(counts)) names[name] = true;
  }
  return Object.keys(names).sort((a, b) => cohortRank(a) - cohortRank(b) || a.localeCompare(b));
};

/** Past six cohorts the rest fold into one series rather than inventing colours. */
export const cohortSeries = (names: string[], mode: ChartMode): Series[] => {
  const folds = names.length > MAX_COHORT_SERIES;
  const shown = folds ? names.slice(0, MAX_COHORT_SERIES - 1) : names;
  const series = shown.map((key, index) => ({ key, colour: COHORT_COLOURS[mode][index] }));
  if (folds) series.push({ key: OTHER_COHORTS, colour: COHORT_COLOURS[mode][MAX_COHORT_SERIES - 1] });
  return series;
};

export const noCohortSeries = (mode: ChartMode): Series => ({ key: NO_COHORT, colour: NEUTRAL[mode] });

export const armSeries = (mode: ChartMode): Series[] =>
  ARMS.map((arm) => ({ key: arm.label, colour: ARM_COLOURS[mode][arm.key] }));

export const foldCounts = (counts: Record<string, number>, series: Series[]): Record<string, number> => {
  const folded: Record<string, number> = {};
  const known = new Set(series.map((s) => s.key));
  for (const s of series) folded[s.key] = 0;
  for (const [name, count] of Object.entries(counts)) {
    const key = known.has(name) ? name : OTHER_COHORTS;
    folded[key] = (folded[key] ?? 0) + count;
  }
  return folded;
};

export interface CampaignRow {
  label: string;
  campaign: string;
  status: string;
  total: number;
  counts: Record<string, number>;
}

export const campaignRows = (campaigns: CampaignCohortCounts[], series: Series[]): CampaignRow[] =>
  campaigns.map((c) => {
    const counts = { ...foldCounts(c.cohorts, series), [NO_COHORT]: c.unplaced };
    return {
      label: `${c.retailerName} #${c.campaignId}`,
      campaign: c.campaignName,
      status: c.status,
      total: sum(Object.values(counts)),
      counts,
    };
  });

const panelLabel = (panel: PanelArmCounts): string =>
  panel.version > 1 ? `${panel.retailerName} v${panel.version}` : panel.retailerName;

export interface PanelSplitRow {
  label: string;
  total: number;
  counts: Record<string, number>;
  note: string;
}

export const panelSplitRows = (panels: PanelArmCounts[]): PanelSplitRow[] =>
  panels.map((panel) => {
    const [test, control] = ARMS.map((arm) => sum(Object.values(panel.arms[arm.key] ?? {})));
    const total = test + control;
    return {
      label: panelLabel(panel),
      total,
      counts: { Test: test, Control: control },
      note: `${control} in control (${share(control, total)})`,
    };
  });

export interface ArmMixRow {
  label: string;
  members: number;
  counts: Record<string, number>;
  shares: Record<string, number>;
}

/** One row per arm that has members, with each cohort's share of the arm as drawn. */
export const armMixRows = (panels: PanelArmCounts[], series: Series[]): ArmMixRow[] =>
  panels.flatMap((panel) =>
    ARMS.map((arm) => {
      const counts = foldCounts(panel.arms[arm.key] ?? {}, series);
      const members = sum(Object.values(counts));
      const shares: Record<string, number> = {};
      for (const [key, count] of Object.entries(counts)) shares[key] = members > 0 ? count / members : 0;
      return { label: `${panelLabel(panel)} · ${arm.label.toLowerCase()}`, members, counts, shares };
    }).filter((row) => row.members > 0)
  );

export interface FunnelRow {
  label: string;
  value: number;
  note: string;
}

export const funnelRows = (funnel: CohortFunnel): FunnelRow[] =>
  FUNNEL_STAGES.map((stage) => {
    const value = funnel[stage.key];
    return { label: stage.label, value, note: `${value} (${share(value, funnel.consented)})` };
  });

export interface TimelineRow extends CohortTimelineDay {
  label: string;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const dayLabel = (day: string): string => {
  const [, month, date] = day.split("-").map(Number);
  return `${date} ${MONTHS[month - 1]}`;
};

export const timelineRows = (timeline: CohortTimelineDay[]): TimelineRow[] =>
  timeline.map((d) => ({ ...d, label: dayLabel(d.day) }));
